import {Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {AppStateService, AppWindowState} from '../../services/appState/app-state.service';
import {AuthService} from '../../../auth/services/auth/auth.service';
import {AppService} from '../../services/app/app.service';
import {Tab} from '../../../tabbing/data/tab';
import {TabbingService} from '../../../tabbing/services/tabbing.service';
import {PleaseWaitService} from '../../services/please-wait/please-wait.service';
import {HarService} from '../../../logging/services/har.service';
import {environment} from '../../../../environments/environment';
import {LoggerService} from '../../../logging/services/logger.service';
import {NativeService} from '../../services/native/native.service';
import {NavigationService} from '../../services/navigation.service';
import {LogLevel} from '../../../logging/data/logLevel';
import {LogSourceType} from '../../../logging/data/logSourceType';
import {LogSource} from '../../../logging/data/logSource';

/**
 * Angular default mappings
 */
@Component({
  selector: 'app-title-bar',
  templateUrl: './title-bar.component.html',
  styleUrls: ['./title-bar.component.scss']
})
export class TitleBarComponent implements OnInit {

  @ViewChild('horizontalScroll') horizontalScroll: ElementRef;
  /**
   * Should we show the settings button
   * false on macs
   */
  @Input() showSettings = true;
  @Input() headlessMode = false;
  /**
   * Is the settings panel open?
   */
  settingsPanelOpen = false;
  selectedTab: Tab;
  frontendUrl: string = environment.frontendUrl;

  AppWindowState = AppWindowState;

  constructor(public appService: AppService,
              public nativeService: NativeService,
              public appStateService: AppStateService,
              public tabbingService: TabbingService,
              public pleaseWaitService: PleaseWaitService,
              public harService: HarService,
              public loggerService: LoggerService,
              public authService: AuthService,
              private navigationService: NavigationService) {
    this.nativeService = nativeService;
    this.appStateService = appStateService;
    this.appService = appService;
    this.tabbingService = tabbingService;
    this.authService = authService;
  }

  /**
   * Exits the apps GRACEFULLY™️
   */
  async exit() {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.TitleBarComponent, 'Exiting app');
    this.nativeService.closeApp();
  }

  /**
   * Minimizes the desktop app
   */
  minimize() {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.TitleBarComponent, 'Minimizing app');
    this.nativeService.minimizeApp();
  }

  /**
   * Maximizes the desktop app
   */
  maximize() {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.TitleBarComponent, 'Maximizing app');
    this.nativeService.maximizeApp();
  }

  /**
   * Default initialization
   */
  ngOnInit() {
  }

  /**
   * Opens the settings panel
   */
  openSettings() {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.TitleBarComponent, 'Opening settings panel');
    this.settingsPanelOpen = true;
  }

  scrollTabs(event: any) {
    this.horizontalScroll.nativeElement.scrollLeft += event.deltaY;
    event.preventDefault();
  }

  addNewTab() {
    if (this.tabbingService.tabs.length >= 10 ){
      this.appService.showInfoMessage('Maximum tabs limit has been reached.');
    } else {
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.TitleBarComponent, 'Adding new tab');
      const tab = this.tabbingService.addTab();
      setTimeout(() => {
        this.navigationService.navigate(tab, [tab.navigationData.url]);
      }, 0);
    }
  }

  selectTab(tab: Tab) {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.TitleBarComponent, `Selecting tab ${tab.navigationData.url}`);
    this.tabbingService.visibleTab = tab;
  }

  closeTab(tab: Tab) {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.TitleBarComponent, `Closing tab ${tab.navigationData.url}`);
    for (let i = 0; i < this.tabbingService.tabs.length; i++) {
      if (this.tabbingService.tabs[i] === tab) {
        this.tabbingService.tabs.splice(i, 1);
        break;
      }
    }
    if (this.tabbingService.tabs.length <= 0) {
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.TitleBarComponent, 'No more tabs, adding new tab');
      this.addNewTab();
    }
    if (tab === this.tabbingService.visibleTab) {
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.TitleBarComponent, 'Selected tab was closed, selecting first tab');
      this.tabbingService.visibleTab = this.tabbingService.tabs[0];
    }
  }

  copyUrl() {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.TitleBarComponent, 'Copying url');
    this.appService.showInfoMessage('Link copied to clipboard');
    navigator.clipboard.writeText(this.frontendUrl + this.tabbingService.visibleTab.navigationData.url);
  }

  pasteUrl() {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.TitleBarComponent, 'Pasting url');
    navigator.clipboard.readText().then((clipText) => {
      if (clipText.includes(this.frontendUrl, 0)) {
        if (this.tabbingService.tabs.length >= 10 ){
          this.appService.showInfoMessage('Maximum tabs limit has been reached. Please close at lease 1 tab.');
        } else {
          const localPath = clipText.replace(this.frontendUrl, '');
          this.navigationService.navigateInNewTab([localPath.substr(1)]);
          this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.TitleBarComponent, `Navigating to ${localPath}`);
        }
      } else {
        this.loggerService.logMessage(LogLevel.WARN, LogSourceType.COMPONENT, LogSource.TitleBarComponent, 'Pasted url is not a valid url');
        this.appService.showErrorMessage('The pasted link is not a GODH Link');
      }
    }).catch(() => {
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.COMPONENT, LogSource.TitleBarComponent, 'Pasted url is not a valid url');
      this.appService.showErrorMessage('Error reading from the clipboard');
    });
  }

  async cleanCache() {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.TitleBarComponent, 'Cleaning cache');
    await this.nativeService.reloadApp();
  }

  isMac() {
    return this.nativeService.isMac;
  }
}
